import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { Menu, X } from "lucide-react";
import { useT, useLang } from "../lib/i18n";

/** French flag (3 vertical bars) */
const FlagFR = ({ size = 16 }) => (
  <svg viewBox="0 0 24 16" width={size} height={(size * 16) / 24} className="block rounded-[2px]">
    <rect x="0"  y="0" width="8"  height="16" fill="#0055A4" />
    <rect x="8"  y="0" width="8"  height="16" fill="#FFFFFF" />
    <rect x="16" y="0" width="8"  height="16" fill="#EF4135" />
  </svg>
);
/** Union Jack (refined) */
const FlagEN = ({ size = 16 }) => (
  <svg viewBox="0 0 24 16" width={size} height={(size * 16) / 24} className="block rounded-[2px]">
    <rect width="24" height="16" fill="#012169" />
    <path d="M0,0 L24,16 M24,0 L0,16" stroke="#FFFFFF" strokeWidth="3" />
    <path d="M0,0 L24,16 M24,0 L0,16" stroke="#C8102E" strokeWidth="1.5" />
    <path d="M12,0 V16 M0,8 H24" stroke="#FFFFFF" strokeWidth="4" />
    <path d="M12,0 V16 M0,8 H24" stroke="#C8102E" strokeWidth="2.4" />
  </svg>
);

const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex items-center gap-1 ${compact ? "" : "ml-3"} bg-bone/5 backdrop-blur-sm rounded-full p-1`}
      data-testid="lang-switcher"
    >
      <button
        onClick={() => setLang("fr")}
        aria-label="Français"
        title="Français"
        data-testid="lang-fr"
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase transition-all ${
          lang === "fr" ? "bg-bone text-ink" : "text-bone/70 hover:text-bone"
        }`}
      >
        <FlagFR /> FR
      </button>
      <button
        onClick={() => setLang("en")}
        aria-label="English"
        title="English"
        data-testid="lang-en"
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase transition-all ${
          lang === "en" ? "bg-bone text-ink" : "text-bone/70 hover:text-bone"
        }`}
      >
        <FlagEN /> EN
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const t = useT();

  const links = [
    { to: "/",          label: t("nav.home"),      key: "home" },
    { to: "/portfolio", label: t("nav.portfolio"), key: "portfolio" },
    { to: "/about",     label: t("nav.about"),     key: "about" },
    { to: "/boutique",  label: t("nav.shop"),      key: "shop" },
    { to: "/contact",   label: t("nav.contact"),   key: "contact" },
  ];

  /* eslint-disable */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  /* eslint-enable */

  const lightPage = location.pathname === "/boutique" || location.pathname === "/contact";
  const useDark = scrolled || lightPage;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          useDark
            ? "bg-[#0D0D0D]/90 backdrop-blur-md border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-[76px]">
          <Link to="/" data-testid="nav-logo" data-cursor="ACCUEIL">
            <Logo size={26} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key}`}
                data-cursor=""
                end={l.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-[11px] tracking-[0.22em] uppercase text-bone/80 hover:text-bone transition-colors ${
                    isActive ? "active text-bone" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <LanguageSwitcher />
          </nav>
          <div className="md:hidden flex items-center gap-3">
            <LanguageSwitcher compact />
            <button
              data-testid="mobile-menu-toggle"
              data-cursor="MENU"
              className="text-bone"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#0D0D0D] md:hidden flex flex-col items-center justify-center gap-10"
          >
            {links.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-${l.key}`}
                  className="font-display text-4xl text-bone"
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
